import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Row } from 'reactstrap';
import { faBorderAll } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router';
import { MiciCaseItem, MiciFilter, QueryGroup } from '../../services/types/MiciCases';
import getCells from './Cells';
import {
  Button,
  PageHeader,
  Section,
  SpinnerArea,
  Table,
  TablePagination,
  TabBar,
  CustomDropDown,
  Modal,
} from '../../shared/components';
import { findMapped, findMapId, capitalize } from '../../shared/misc/utils';
import useServiceReducer from '../../shared/hooks/useServiceReducer';
import reducer from './reducer';
import Context from '../App/MainProvider/context';

import miciCasesService from '../../services/miciCasesService';
import ColumnChecks from '../Operations/ColumnChecks';
import Highlights from '../Operations/Highlights';

const MiciCases: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const querySrting = new URLSearchParams(location.search);
  const q = (key: string) => querySrting.get(key);
  const operationId = q('operationId') ? +(q('operationId') || 0) : undefined;

  const { service } = useContext(Context);
  const initialHidenColumns = operationId
    ? [
        'country',
        'operationNumber',
        'operationName',
        'operationRegion',
        'operationClosingDate',
        'operationDisbursementDate',
        'causes',
      ]
    : ['country', 'operationRegion', 'operationClosingDate', 'operationDisbursementDate'];

  const initialFilter: MiciFilter = {
    queryGroup: QueryGroup.All,
    pageNumber: 1,
    pageSize: 10,
    sortField: 'Status',
    sortDescending: true,
    operationId,
    hiddenColumns: initialHidenColumns,
  };

  const [data, loading, filter, dispatch] = useServiceReducer(
    miciCasesService.getMiciCases,
    reducer,
    initialFilter
  );

  const [excelLoading, setExcelLoading] = useState(false);

  const [hiddenColumns, setHideColumns] = useState<string[]>(initialHidenColumns);

  const queryGroups = [
    { id: 1, text: t('all'), htmlId: 'tabAll', mapped: QueryGroup.All },
    // { id: 2, text: t('miciCaseStages.claims'), htmlId: 'tabClaims', mapped: QueryGroup.Claims },
    {
      id: 3,
      text: t('miciCaseStages.registration'),
      htmlId: 'tabRegistration',
      mapped: QueryGroup.Registration,
    },
    {
      id: 4,
      text: t('miciCaseStages.eligibility'),
      htmlId: 'tabEligibility',
      mapped: QueryGroup.Eligibility,
    },
    {
      id: 5,
      text: t('miciCaseStages.consultation'),
      htmlId: 'tabConsultation',
      mapped: QueryGroup.Consultation,
    },
    {
      id: 6,
      text: t('miciCaseStages.complianceReview'),
      htmlId: 'tabCompReview',
      mapped: QueryGroup.CompReview,
    },
    {
      id: 7,
      text: t('miciCaseStages.closed'),
      htmlId: 'tabClosed',
      mapped: QueryGroup.Closed,
    },
  ];

  const setQueryGroup = (tabId: number) => {
    const queryGroup = findMapped(queryGroups, tabId);
    if (queryGroup !== QueryGroup.All && queryGroup !== QueryGroup.Closed) {
      setHideColumns([...hiddenColumns, 'stage']);
    } else {
      const columns = [...hiddenColumns].filter((x) => x !== 'stage');
      setHideColumns(columns);
    }

    dispatch({ type: 'QUERY_GROUP', value: queryGroup });
  };

  const changePage = (page: number) => dispatch({ type: 'PAGE', value: page });

  const changePageSize = (size: number) => dispatch({ type: 'PAGE_SIZE', value: size });

  const setColumnOrder = (sortField: string, sortDescending: boolean) =>
    dispatch({ type: 'ORDER', column: sortField, descending: sortDescending });

  const exportExcel = () => {
    const pascalHiddenColumns = hiddenColumns.map((x) => capitalize(x));
    const excelParam = {
      ...filter,
      pageSize: 0,
      pageNumber: 1,
      hiddenColumns: pascalHiddenColumns,
    };
    service(() => miciCasesService.exportExcel(excelParam), { loader: setExcelLoading });
  };

  const [showCausesModal, setShowCausesModal] = useState(false);
  const [currentCausesType, setcurrentCausesType] = useState<string>('causes');
  const [currentCausesText, setCurrentCausesText] = useState<string>();

  const showCause = (causesType: string, causesText: string) => {
    setcurrentCausesType(causesType);
    setCurrentCausesText(causesText);
    setShowCausesModal(true);
  };

  const sethiddenColumns = (id: string, value: boolean) => {
    if (!value) {
      setHideColumns([...hiddenColumns, id]);
    } else {
      const columns = [...hiddenColumns].filter((x) => x !== id);
      setHideColumns(columns);
    }
  };

  const title = `${
    operationId && data && data.list && data.list.length > 0
      ? `${data.list[0].operationNumber} / `
      : ''
  }${t('miciCases')}`;

  return (
    <>
      <Container className="dashboard">
        <PageHeader title={title} noBack />
        <Row>
          <Section>
            {!operationId && (
              <div className="header-controls">
                <TabBar
                  id="queryGroupTabBar"
                  tabSelected={findMapId(queryGroups, filter.queryGroup)}
                  setTabSelected={setQueryGroup}
                  headers={queryGroups}
                />
              </div>
            )}
            <div>
              <div className="details-bar">
                <div className="content">
                  <h4>
                    {data &&
                      t('showingMiciCases', { from: data.from, to: data.to, total: data.total })}
                  </h4>
                </div>
                {data && <Highlights highlightGroups={data.highlightGroups} withoutCategory />}
              </div>
              <div className="button-container-bar">
                <div className="button-container-bar--left">{/* <h5>{t('view')}:</h5> */}</div>
                <div className="button-container-bar--right">
                  <CustomDropDown text={t('columns')} icon={faBorderAll} className="oper-columns">
                    <ColumnChecks<MiciCaseItem>
                      cells={getCells([], showCause, operationId).filter(
                        (x) => x.name !== 'number' && x.name !== 'operationNumber'
                      )}
                      removeColums={[]}
                      hideColumns={hiddenColumns}
                      setHideColumn={sethiddenColumns}
                    />
                  </CustomDropDown>
                  <Button
                    htmlId="btnExcel"
                    text={t('exportToExcel')}
                    onClick={exportExcel}
                    icon="file-excel"
                    className="btn-tertiary"
                  />
                </div>
              </div>
              <SpinnerArea loading={loading || excelLoading}>
                {data && (
                  <>
                    <Table
                      data={data.list}
                      cells={getCells(hiddenColumns, showCause, operationId)}
                      keyFunc={(x) => x.id}
                      sortData={filter}
                      changeSort={setColumnOrder}
                    />

                    <TablePagination
                      pageSize={filter.pageSize}
                      pageNumber={filter.pageNumber}
                      total={data.total}
                      onChangePage={changePage}
                      onChangeSize={changePageSize}
                    />
                  </>
                )}
              </SpinnerArea>
            </div>
          </Section>
        </Row>
      </Container>
      <Modal
        title={t(currentCausesType)}
        visible={showCausesModal}
        setVisible={() => setShowCausesModal(false)}
        actions={[{ text: 'Close', action: () => setShowCausesModal(false) }]}
        customClass="extra_wide-modal"
        onClosed={() => setShowCausesModal(false)}
      >
        <div className="history-content flow-history mb-0 table-responsive">
          <table>
            <tbody>
              <tr>
                <td>{currentCausesText}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Modal>
    </>
  );
};

export default MiciCases;
